import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/tommy-hour-logo.jpg";

const navLinks = [
  { href: "#home", label: "Home", isSection: true },
  { href: "#about", label: "About", isSection: true },
  { href: "#drinks", label: "Drinks", isSection: true },
  { href: "#food", label: "Food", isSection: true },
  { href: "#events", label: "Events", isSection: true },
  { href: "#gallery", label: "Gallery", isSection: true },
  { href: "/contact", label: "Contact", isSection: false },
];

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerEl = document.querySelector("header");
    const headerOffset = headerEl ? headerEl.getBoundingClientRect().height + 16 : 96;
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });
  };

  const scrollToSection = (href: string) => {
    const id = href.startsWith("#") ? href.slice(1) : href;
    const isHomePage = location.pathname === "/";

    // Close the mobile menu first
    setIsOpen(false);

    if (!isHomePage) {
      // Navigate to home page with the section hash
      navigate(`/#${id}`);
    } else {
      // Already on home page, just scroll
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToElement(id);
        });
      });
    }
  };

  // Handle hash navigation after page loads
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.slice(1);
      // Wait for page to fully render
      const timeout = setTimeout(() => {
        scrollToElement(id);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [location.pathname, location.hash]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <motion.a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#home");
            }}
            className="flex items-center gap-3"
            whileHover={{ scale: 1.02 }}
          >
            <img
              src={logo}
              alt="Tommy Hour Logo"
              className="h-12 w-12 rounded-full object-cover ring-2 ring-primary/30"
            />
            <span className="font-display text-2xl tracking-wider text-foreground hidden sm:block">
              TOMMY HOUR
            </span>
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isSection ? (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(link.href);
                  }}
                  className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors duration-300 relative group"
                  whileHover={{ y: -2 }}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </motion.a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors duration-300 relative group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                </Link>
              )
            ))}
          </div>

          {/* Desktop CTA & Social */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/60 hover:text-primary transition-colors"
              aria-label="Follow us on Instagram"
            >
              <Instagram className="w-5 h-5" aria-hidden="true" />
            </a>
            <a
              href="tel:+66123456789"
              className="text-foreground/60 hover:text-primary transition-colors"
              aria-label="Call us at +66 123 456 789"
            >
              <Phone className="w-5 h-5" aria-hidden="true" />
            </a>
            <Button
              variant="hero"
              size="sm"
              onClick={() => scrollToSection("#reservation")}
            >
              Reserve
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-foreground p-2"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-border/20"
          >
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link, index) => (
                  link.isSection ? (
                    <motion.a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="text-lg font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                    >
                      {link.label}
                    </motion.a>
                  ) : (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                        className="block text-lg font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  )
                ))}
                <Button
                  variant="hero"
                  size="lg"
                  className="mt-4"
                  onClick={() => scrollToSection("#reservation")}
                >
                  Reserve a Table
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
